import type { DailyMetrics, HistoryRow, ProductRecord } from './types'

export const emptyMetrics = (date = ''): DailyMetrics => ({
  date,
  gmv: 0,
  soldItemCount: 0,
  orderCount: 0,
  customerCount: 0,
  conversionRate: 0,
  averageOrderValue: 0,
  averageItemPrice: 0,
  productVisitors: 0,
  productPageviews: 0,
  searchImpressions: 0,
  searchClicks: 0,
  cartItemCount: 0,
  cancelRefundAmount: 0,
  cancelRefundItemCount: 0,
})

export const rowToMetrics = (row?: HistoryRow, date = ''): DailyMetrics => {
  if (!row) return emptyMetrics(date)
  return {
    date: row[0],
    gmv: row[1],
    soldItemCount: row[2],
    orderCount: row[3],
    customerCount: row[4],
    conversionRate: row[5],
    averageOrderValue: row[6],
    averageItemPrice: row[7],
    productVisitors: row[8],
    productPageviews: row[9],
    searchImpressions: row[10],
    searchClicks: row[11],
    cartItemCount: row[12],
    cancelRefundAmount: row[13],
    cancelRefundItemCount: row[14],
  }
}

export const metricsOn = (product: ProductRecord, date: string) =>
  rowToMetrics(product.history.find((row) => row[0] === date), date)

export const previousDateOf = (dates: string[], date: string) => {
  const index = dates.indexOf(date)
  return index > 0 ? dates[index - 1] : ''
}

export const deltaRate = (current: number, previous: number) => {
  if (!previous) return current > 0 ? null : 0
  return (current / previous - 1) * 100
}

export const isActive = (metrics: DailyMetrics) =>
  metrics.gmv > 0 || metrics.soldItemCount > 0

export const money = (value: number) =>
  `¥${Number(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export const itemUnitPrice = (gmv: number, soldItemCount: number) =>
  soldItemCount > 0 ? gmv / soldItemCount : null

export const integer = (value: number) => Number(value || 0).toLocaleString('zh-CN')

export const percent = (value: number) => `${Number(value || 0).toFixed(2)}%`

export const shortDate = (date: string) => date.slice(5)

export const formatTimestamp = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date).replaceAll('/', '-')
}

export const categoryName = (product: ProductRecord) =>
  product.category[2] || product.category[1] || product.category[0] || '未分类'

export const sumMetrics = (rows: DailyMetrics[], date = '') => {
  const total = rows.reduce((result, item) => ({
    ...result,
    gmv: result.gmv + item.gmv,
    soldItemCount: result.soldItemCount + item.soldItemCount,
    orderCount: result.orderCount + item.orderCount,
    customerCount: result.customerCount + item.customerCount,
    productVisitors: result.productVisitors + item.productVisitors,
    productPageviews: result.productPageviews + item.productPageviews,
    searchImpressions: result.searchImpressions + item.searchImpressions,
    searchClicks: result.searchClicks + item.searchClicks,
    cartItemCount: result.cartItemCount + item.cartItemCount,
    cancelRefundAmount: result.cancelRefundAmount + item.cancelRefundAmount,
    cancelRefundItemCount: result.cancelRefundItemCount + item.cancelRefundItemCount,
  }), emptyMetrics(date))
  return {
    ...total,
    conversionRate: total.productVisitors ? total.customerCount / total.productVisitors * 100 : 0,
    averageOrderValue: total.customerCount ? total.gmv / total.customerCount : 0,
    averageItemPrice: total.soldItemCount ? total.gmv / total.soldItemCount : 0,
  }
}

export const metricsAcrossDates = (product: ProductRecord, dates: string[]) => {
  if (dates.length === 1) return metricsOn(product, dates[0])
  const dateSet = new Set(dates)
  const rows = product.history
    .filter((row) => dateSet.has(row[0]))
    .map((row) => rowToMetrics(row))
  const label = dates.length > 1 ? `${dates[0]}~${dates[dates.length - 1]}` : dates[0] || ''
  return sumMetrics(rows, label)
}
